import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";
import { ModalTypes } from "configs/types";
import Button from "components/buttons/Button";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import { Modal } from "components/Modal/Modal";
import { getBusinessErrorMessage } from "helpers/handleApiError";
import { useAppDispatch, useAppSelector } from "store/index";
import { closeModal, openModal } from "store/modals/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import { resetConfiscationsList } from "store/payments/confiscations/slice";
import { resetSelectPlayer } from "store/payments/selectPlayer/slice";
import {
  getPaymentInfo,
  getPlayerItem,
  handleDepositsClear,
  handleInfoClear,
} from "store/players/list/slice";

import infoIcon from "assets/icons/info.svg";
import userIcon from "assets/icons/user.svg";
import styles from "./FinanceActionModal.module.scss";

type ConfiscationType =
  | "fund_confiscation"
  | "other_debit"
  | "balance_transfer_debit";

type FormField =
  | "confiscationType"
  | "amount"
  | "transactionCode"
  | "internalComment";

type FormErrors = Partial<Record<FormField, string>>;

type ConfiscationPayload = {
  userId: number;
  amount: string;
  confiscationType: ConfiscationType;
  transactionCode: string;
  internalComment: string;
};

const CONFISCATION_TYPES: ConfiscationType[] = [
  "fund_confiscation",
  "other_debit",
  "balance_transfer_debit",
];

const DECIMAL_PATTERN = /^\d+(?:\.\d+)?$/;
const MIN_AMOUNT = 0.01;
const MAX_TRANSACTION_CODE_LENGTH = 191;
const MAX_INTERNAL_COMMENT_LENGTH = 1000;

const sanitizeDecimal = (value: string) => {
  const sanitized = value.replace(/[^\d.]/g, "");
  const [integerPart = "", ...decimalParts] = sanitized.split(".");
  const hasDecimalPoint = sanitized.includes(".");
  const decimalPart = decimalParts.join("");
  const normalizedIntegerPart =
    hasDecimalPoint && integerPart === "" ? "0" : integerPart;

  return hasDecimalPoint
    ? `${normalizedIntegerPart}.${decimalPart}`
    : normalizedIntegerPart;
};

const CreateConfiscation: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const player = useAppSelector((store) => store.players.payload);

  const [confiscationType, setConfiscationType] = useState<
    ConfiscationType | ""
  >("");
  const [amount, setAmount] = useState("");
  const [transactionCode, setTransactionCode] = useState("");
  const [internalComment, setInternalComment] = useState("");
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setSubmitting] = useState(false);

  const userId = Number(searchParams.get("entityId"));
  const returnToList = searchParams.get("returnTo") === "list";
  const purpose = searchParams.get("purpose") ?? "confiscation";
  const currency = player?.currency ?? "";
  const realMoneyBalance =
    typeof player?.balanceReal === "number" ? player.balanceReal : undefined;

  const confiscationOptions = useMemo(
    () =>
      CONFISCATION_TYPES.map((value) => ({
        value,
        label: t(`confiscationType_${value}`),
      })),
    [t],
  );

  useEffect(() => {
    if (userId && player?.id !== userId) {
      dispatch(getPlayerItem(userId));
    }
  }, [dispatch, player?.id, userId]);

  const handleClose = () => {
    if (returnToList) {
      navigate(
        `${location.pathname}?activeModal=${ModalTypes.selectPlayer}&purpose=${purpose}`,
      );
      dispatch(openModal({ activeModal: ModalTypes.selectPlayer }));
      return;
    }

    navigate(
      `${location.pathname}?activeModal=${ModalTypes.player}&entityId=${searchParams.get("entityId")}&tab=finance`,
    );
    dispatch(openModal({ activeModal: ModalTypes.player }));
  };

  const clearError = (field: FormField) => {
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const validate = () => {
    const nextErrors: FormErrors = {};

    if (!confiscationType) {
      nextErrors.confiscationType = t("requiredField");
    }

    if (!amount) {
      nextErrors.amount = t("requiredField");
    } else if (!DECIMAL_PATTERN.test(amount)) {
      nextErrors.amount = t("invalidCompensationAmount");
    } else if (Number(amount) < MIN_AMOUNT) {
      nextErrors.amount = t("invalidCompensationAmount");
    } else if (
      realMoneyBalance !== undefined &&
      Number(amount) > realMoneyBalance
    ) {
      nextErrors.amount = t("insufficientPlayerFunds");
    }

    const normalizedTransactionCode = transactionCode.trim();
    if (!normalizedTransactionCode) {
      nextErrors.transactionCode = t("requiredField");
    } else if (normalizedTransactionCode.length > MAX_TRANSACTION_CODE_LENGTH) {
      nextErrors.transactionCode = t("transactionCodeMaxLength191");
    }

    const normalizedInternalComment = internalComment.trim();
    if (!normalizedInternalComment) {
      nextErrors.internalComment = t("requiredField");
    } else if (normalizedInternalComment.length > MAX_INTERNAL_COMMENT_LENGTH) {
      nextErrors.internalComment = t("internalCommentMaxLength1000");
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const getBackendError = (error: unknown) =>
    getBusinessErrorMessage(error, t, "confiscationCreateError");

  const handleCreate = async () => {
    if (!userId || isSubmitting || !validate() || !confiscationType) return;

    setSubmitting(true);
    try {
      const payload: ConfiscationPayload = {
        userId,
        amount,
        confiscationType,
        transactionCode: transactionCode.trim(),
        internalComment: internalComment.trim(),
      };

      await instance.post(apiRoutes.paymentConfiscation, payload);

      dispatch(
        showNotification({
          title: t("success"),
          message: t("confiscationCreated"),
          type: NotificationTypes.success,
        }),
      );

      if (returnToList) {
        dispatch(resetConfiscationsList());
        dispatch(resetSelectPlayer());
        navigate(location.pathname);
        dispatch(closeModal());
        return;
      }

      dispatch(handleDepositsClear());
      dispatch(handleInfoClear());
      await Promise.all([
        dispatch(getPlayerItem(userId)),
        dispatch(getPaymentInfo(String(userId))),
      ]);
      handleClose();
    } catch (error) {
      dispatch(
        showNotification({
          title: t("error"),
          message: getBackendError(error),
          type: NotificationTypes.error,
        }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal onClose={handleClose}>
      <Modal.Header className={styles.modalHeader}>
        <p>{t("createConfiscation")}</p>
      </Modal.Header>
      <Modal.Content className={styles.modalContent}>
        <div className={styles.form}>
          <div className={styles.playerBar}>
            <img
              src={userIcon}
              alt=""
              aria-hidden="true"
              className={styles.playerBarIcon}
            />
            <div className={styles.playerBarMeta}>
              <div className={styles.playerBarItem}>
                <span className={styles.playerBarLabel}>{t("playerId")}:</span>
                <span className={styles.playerBarValue}>
                  {player?.id ?? userId ?? "—"}
                </span>
              </div>
              <span className={styles.playerBarDivider} aria-hidden="true" />
              <div className={styles.playerBarItem}>
                <span className={styles.playerBarLabel}>{t("nickname")}:</span>
                <span className={styles.playerBarValue}>
                  {player?.nickname || "—"}
                </span>
              </div>
            </div>
          </div>

          <DefaultDropdown
            label={t("transactionType")}
            value={confiscationType}
            placeholder={t("selectTransactionType")}
            options={confiscationOptions}
            onSelect={(value) => {
              setConfiscationType(value as ConfiscationType);
              clearError("confiscationType");
            }}
            isRequired
            hasError={Boolean(errors.confiscationType)}
            disabled={isSubmitting}
          />

          <InnerInput
            type="text"
            label={t("amount")}
            placeholder={t("enterAmount")}
            value={amount}
            onChange={(value) => {
              setAmount(sanitizeDecimal(value));
              clearError("amount");
            }}
            id="confiscationAmount"
            inputMode="decimal"
            suffix={currency || undefined}
            isRequired
            hasError={Boolean(errors.amount)}
            title={errors.amount}
            disabled={isSubmitting}
          />

          <InnerInput
            type="text"
            label={t("transactionCode")}
            placeholder={t("confiscationTransactionCodePlaceholder")}
            value={transactionCode}
            onChange={(value) => {
              setTransactionCode(value);
              clearError("transactionCode");
            }}
            id="confiscationTransactionCode"
            isRequired
            hasError={Boolean(errors.transactionCode)}
            title={errors.transactionCode}
            disabled={isSubmitting}
          />

          <InnerInput
            type="textarea"
            label={t("internalComment")}
            placeholder={t("confiscationInternalCommentPlaceholder")}
            value={internalComment}
            onChange={(value) => {
              setInternalComment(value);
              clearError("internalComment");
            }}
            id="confiscationInternalComment"
            rows={4}
            isRequired
            hasError={Boolean(errors.internalComment)}
            title={errors.internalComment}
            disabled={isSubmitting}
          />

          <div className={styles.notice}>
            <img src={infoIcon} alt="" aria-hidden="true" />
            <span>{t("confiscationNotice")}</span>
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Button
          isSecondary
          txt={returnToList ? t("back") : t("cancel")}
          handleClick={handleClose}
          disabled={isSubmitting}
        />
        <Button
          isDelete
          handleClick={handleCreate}
          txt={t("create")}
          disabled={!userId || isSubmitting}
          isLoad={isSubmitting}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default CreateConfiscation;
